import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFamilyContext } from '../context/FamilyContext';

const { FiX, FiCalendar, FiClock, FiMapPin, FiUser, FiRepeat, FiType } = FiIcons;

export default function EventModal({ selectedDate, onClose, onSave }) {
  const { state } = useFamilyContext();
  const { familyMembers } = state;
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '',
    time: '12:00',
    location: '',
    assignedTo: familyMembers[0]?.id || '',
    type: 'event',
    allDay: false,
    recurring: 'none'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim()) return;

    const eventData = {
      ...formData,
      date: new Date(formData.date).toISOString()
    };

    onSave(eventData);
    onClose();
  };

  const eventTypes = [
    { value: 'event', label: 'Event' },
    { value: 'appointment', label: 'Termin' },
    { value: 'reminder', label: 'Erinnerung' },
    { value: 'birthday', label: 'Geburtstag' }
  ];

  const recurringOptions = [
    { value: 'none', label: 'Einmalig' },
    { value: 'daily', label: 'Täglich' },
    { value: 'weekly', label: 'Wöchentlich' },
    { value: 'biweekly', label: 'Alle 2 Wochen' },
    { value: 'monthly', label: 'Monatlich' },
    { value: 'yearly', label: 'Jährlich' }
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
        
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative bg-white rounded-xl shadow-xl max-w-md w-full dark:bg-gray-800"
        >
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Neues Event erstellen
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300">
                Titel *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="Event-Name eingeben..."
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300">
                Beschreibung
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="3"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="Zusätzliche Details..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
                {/* Date */}
                <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300">
                    <SafeIcon icon={FiCalendar} className="w-4 h-4 inline mr-2" />
                    Datum *
                </label>
                <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    required
                />
                </div>

                {/* Time */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300">
                        <SafeIcon icon={FiClock} className="w-4 h-4 inline mr-2" />
                        Uhrzeit
                    </label>
                    <div className="flex items-center space-x-2">
                        <input
                            type="time"
                            value={formData.time}
                            onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                            disabled={formData.allDay}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 disabled:opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        />
                    </div>
                </div>
            </div>

            {/* All Day Toggle */}
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="allDay"
                checked={formData.allDay}
                onChange={(e) => setFormData({ ...formData, allDay: e.target.checked })}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600"
              />
              <label htmlFor="allDay" className="text-sm text-gray-700 dark:text-gray-300">
                Ganztägig
              </label>
            </div>

            <div className="grid grid-cols-2 gap-4">
                {/* Type */}
                <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300">
                    <SafeIcon icon={FiType} className="w-4 h-4 inline mr-2" />
                    Typ
                </label>
                <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                    {eventTypes.map(type => (
                    <option key={type.value} value={type.value}>
                        {type.label}
                    </option>
                    ))}
                </select>
                </div>

                {/* Recurring */}
                <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300">
                    <SafeIcon icon={FiRepeat} className="w-4 h-4 inline mr-2" />
                    Wiederholung
                </label>
                <select
                    value={formData.recurring}
                    onChange={(e) => setFormData({ ...formData, recurring: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                    {recurringOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>
                        {opt.label}
                    </option>
                    ))}
                </select>
                </div>
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300">
                <SafeIcon icon={FiMapPin} className="w-4 h-4 inline mr-2" />
                Ort
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="Veranstaltungsort..."
              />
            </div>

            {/* Assigned To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300">
                <SafeIcon icon={FiUser} className="w-4 h-4 inline mr-2" />
                Zugewiesen an
              </label>
              <select
                value={formData.assignedTo}
                onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                {familyMembers.map(member => (
                  <option key={member.id} value={member.id}>{member.name}</option>
                ))}
              </select>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Erstellen
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
}